import React, { useRef } from 'react';
import { Formik, Form, Field, FieldArray, ErrorMessage, getIn } from 'formik';
import * as Yup from 'yup';
import { KeenIcon, DefaultTooltip, LazyFormikSelect, DatePicker, CustomOption, CustomSingleValue } from '@/components';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import flatFeesService from '@/services/billing/flatFeesService';
import matterService from '@/services/matter/matterService';
import itemService from '@/services/billing/itemService';
import categoryService from '@/services/billing/expenses/categoryService';
import expenseService from '@/services/billing/expenses/expenseService';
import userService from '@/services/setting/userService';
import invoiceService from '@/services/billing/invoice/invoiceService';
import { toast } from 'sonner';
import { Container } from '@/components/container';
import { useContactOptions, useLazyOptions, useLazyOptionsPerIndex } from '@/hooks';
import { useAbility } from '@/providers/';
import { Can } from '@casl/react';
import { CategoryModal } from '@/pages/billing';
import { InputSanitizers } from '@/utils';
import { PriceInput } from '@/components/form';
import { calculateSubtotal, formatDateToYMD } from '@/utils';
import { useUserOptions } from '@/hooks'

const AddExpense = () => {
    const [dataLoading, setDataLoading] = useState(false)
    const [expenseData, setExpenseData] = useState([]);
    const [userData, setuserData] = useState([]);
    const [itemData, setItemData] = useState([])
    const [createContactLoading, setCreateContactLoading] = useState(false)
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('create');
    const [itemId, setItemId] = useState(null)
    const [selectedData, setSelectedData] = useState(null);
    const [isLoading, setIsLoading] = useState(false)
    const [loading, setLoading] = useState(false)
    const { id } = useParams();
    const expenseId = id;
    const Navigate = useNavigate()
    const { ability } = useAbility();
    const formikRef = useRef();
    const loadUserOptions = useUserOptions({ defaultPageSize: 20 });

    const validationSchema = Yup.object().shape({
        expenses: Yup.array()
            .of(
                Yup.object().shape({
                    contact_id: Yup.string()
                        .required('Contact is required'),
                    matter_id: Yup.string()
                        .required('Matter is required'),
                    category_id: Yup.string()
                        .required('Category is required'),
                    date: Yup.date()
                        .required('Date is required')
                        .typeError('Invalid date format'),
                    quantity: Yup.number()
                        .required('Quantity is required')
                        .min(1, 'Quantity must be at least 1')
                        .typeError('Quantity must be a number'),
                    price: Yup.number()
                        .required('Price is required')
                        .min(1, 'Price cannot be 0 or negative')
                        .typeError('Price must be a number'),
                })
            )
            .max(10, 'Only up to 10 flat fees allowed'),
    });

    const initialValues = {
        expenses: [
            {
                contact_id: expenseData?.contact_id || '',
                contact: expenseData?.contact
                    ? {
                        label: `${expenseData.contact.first_name} ${expenseData.contact.last_name}`,
                        value: expenseData.contact.id,
                        id: expenseData.contact.id
                    }
                    : null,
                matter_id: expenseData?.matter_id || '',
                matter: expenseData?.matter
                    ? {
                        label: expenseData.matter.name,
                        value: expenseData.matter.id,
                        id: expenseData.matter.id
                    }
                    : null,
                category_id: expenseData?.category_id || '',
                category: expenseData?.category
                    ? {
                        label: expenseData.category.category_name,
                        value: expenseData.category.id,
                        id: expenseData.category.id
                    }
                    : null,
                cost_type: expenseData?.cost_type || 'soft cost',
                date: formatDateToYMD(expenseData?.date || ''),
                quantity: expenseData?.quantity || 1,
                rate: expenseData?.rate || '',
                billed_by: expenseData?.billed_by?.id || '',
                billed_by_user: expenseData?.billed_by
                    ? {
                        label: `${expenseData.billed_by.first_name} ${expenseData.billed_by.last_name}`,
                        value: expenseData.billed_by.id,
                        id: expenseData.billed_by.id,
                        email: expenseData.billed_by.email
                    } : null,
                status: expenseData?.status || 'billable',
                description: expenseData?.description || '',
                note: expenseData?.note || '',
            }
        ]
    }
    console.log('--->', expenseData, initialValues)
    const handleModalClose = () => {
        setItemId(null)
        setSelectedData(null)
        setMode('create')
        setOpen(false)
    }

    const handleSubmitCategory = async (formData) => {
        try {
            setIsLoading(true);
            const { data, status } = await categoryService.createCategory(formData);
            if (status) {
                resetCategories()
                setOpen(false);
                refresh();
                toast.success(`Category Created Successfully`, {
                    description: `${data.category_name} Category created successfully.`,

                })
            }
        } catch (error) {
            console.error('Error', error)
            if (error.message == '422 Unprocessable Entity. Validation failed.') {
                const validationErrors = error.errors
                const formattedErrors = Object.fromEntries(
                    Object.entries(validationErrors).map(([key, value]) => [key, value[0]])
                );

                formikRef.current?.setErrors(formattedErrors);
                toast.error('Validation Error.', {
                    description: 'Please check your input. Some fields may be invalid.',
                    action: {
                        label: 'Ok',
                    },
                });
            }
        } finally {
            setIsLoading(false);
        }
    }

    const handleSubmit = async (formData, resetForm, setErrors) => {
        setCreateContactLoading(true);
        try {
            const { data, status } = await expenseService.createExpense(formData);
            if (status) {
                resetForm()
                toast.success('Expense Created.', {
                    description: `Expense Created Successfully.`,
                    action: {
                        label: 'Ok',
                    },
                });
                Navigate('/billing/expense')
            }
        } catch (error) {
            console.error('Error', error)
            if (error.message == '422 Unprocessable Entity. Validation failed.') {
                // Validation for numbers
                const responseErrors = error.errors || {};
                const formattedErrors = {};

                Object.entries(responseErrors).forEach(([key, value]) => {
                    if (key.startsWith('phone_number')) {
                        // Extract index and nested field from e.g. "phone_number.0.number"
                        const match = key.match(/^phone_number\.(\d+)\.(\w+)$/);
                        if (match) {
                            const [, index, field] = match;

                            if (!formattedErrors.phone_number) {
                                formattedErrors.phone_number = [];
                            }

                            formattedErrors.phone_number[Number(index)] = {
                                ...(formattedErrors.phone_number[Number(index)] || {}),
                                [field]: value[0], // or join with \n if multiple messages
                            };
                        }
                    } else {
                        formattedErrors[key] = value[0]; // single error message
                    }
                });
                // console.log(formattedErrors)
                setErrors(formattedErrors)
                if (responseErrors["phone_number.0.number"] || responseErrors["phone_number.1.number"] || responseErrors["phone_number.2.number"]) {
                    toast.warning('Mobile Number Already Taken.', {
                        description: 'Please check you Mobile Numbers. it has has already been taken.',
                        action: {
                            label: 'Ok',
                        },
                    });
                    return
                }
                toast.warning('Validation Error.', {
                    description: 'Please check your input. Some fields may be invalid.',
                    action: {
                        label: 'Ok',
                    },
                });
                return
            }
            toast.error('Oops! Something went wrong.', {
                description: 'An error occurred while creating Expense. Please try again in a moment.',
                action: {
                    label: 'Ok',
                },
            });
        } finally {
            setCreateContactLoading(false);
        }
    };

    const getSingleExpense = async (expenseId) => {
        setDataLoading(true)
        try {
            const { data, status } = await expenseService.getSingleExpense(expenseId);
            if (status) {
                setExpenseData(data)
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Expense.', {
                description: 'An error occurred while fetching Expense. Please try again later.',
            });
        } finally {
            setDataLoading(false)
        }
    }

    const loadContactOptions = useContactOptions({ defaultPageSize: 20 });

    const getAllMattersRelatedToContact = async (contactId, index) => {
        console.log('id', contactId)
        setDataLoading(true)
        try {
            const { data, status } = await invoiceService.getContactMatterRelatedData('contact', contactId);
            if (status) {
                return data.matters.map(m => ({
                    label: m.name,
                    value: m.id.toString(),
                    id: m.id.toString(),
                }));
            }
            return [];
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Matters.', {
                description: 'An error occurred while fetching matters. Please try again later.',
            });
        } finally {
            setDataLoading(false)
        }
    }

    const {
        dataMap: matterOptionsByIndex,
        loadingMap: matterLoadingByIndex,
        load: loadMattersByIndex,
    } = useLazyOptionsPerIndex(async (contactId) => {
        if (!contactId) {
            return
            // await getAllMatters(); // fallback
        }
        return await getAllMattersRelatedToContact(contactId);
    });


    const getAllCategories = async () => {
        setDataLoading(true)
        try {
            const { data, status } = await categoryService.getAllCategory();
            if (status) {
                const Data = data.map(item => ({
                    label: item.category_name,
                    value: item.id.toString(),
                    id: item.id.toString(),
                }));
                setItemData(Data)
                return Data
            }
        } catch (error) {
            console.log(error)
            if (error.status && Array.isArray(error.data)) {
                const fallbackData = error.data.map(item => ({
                    label: item.category_name,
                    value: item.id.toString(),
                    id: item.id.toString(),
                }));
                setItemData(fallbackData);
                // toast.warning('Some issues occurred, but we loaded available data.', {
                //     description: 'Using fallback data from server response.',
                //     
                // });
            } else {
                toast.error('Failed to fetch Expense Categories.', {
                    description: 'An error occurred while fetching expense categories. Please try again later.',

                });
            }
        } finally {
            setDataLoading(false)
        }
    }
    const {
        data: categoryOptions,
        loading: categoryLoading,
        load: loadCategories,
        reset: resetCategories
    } = useLazyOptions(getAllCategories);

    useEffect(() => {
        if (expenseId) { getSingleExpense(expenseId); }
        // getAllCategories();
    }, [])

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, { resetForm, setErrors }) => {
                    handleSubmit(values, resetForm, setErrors);
                    // console.log('Submitted:', values);
                }}
            >
                {({ values, errors, setFieldValue }) => (
                    <Container>
                        <Form className='card'>
                            <div className="card-header">
                                <h3 className="card-title">Create Expense</h3>
                            </div>
                            <FieldArray name="expenses" className="card">
                                {({ push, remove }) => (
                                    <>
                                        {values.expenses.map((_, index) => (
                                            <div key={index} className='card-body'>
                                                <h4 className='text-sm mb-3'>Expense #{index + 1}</h4>

                                                <div className="grid xs:grid-cols-3 gap-5">
                                                    <div>
                                                        <label className="form-label mb-1">Contact <span className='pl-px text-red-600'>*</span></label>
                                                        <LazyFormikSelect
                                                            name={`expenses[${index}].contact_id`}
                                                            placeholder="Select Contact"
                                                            loadOptions={loadContactOptions}
                                                            pageSize={20}
                                                            preloadedOption={initialValues.expenses[index]?.contact}
                                                            onOptionSelect={(selected) => {
                                                                if (selected?.id) {
                                                                    loadMattersByIndex(selected?.id || null, index)
                                                                    setFieldValue(`flat_fees[${index}].matter_id`, '')
                                                                }
                                                            }}
                                                        />
                                                        <ErrorMessage
                                                            name={`expenses[${index}].contact_id`}
                                                            component="div"
                                                            className="text-red-500 text-xs mt-1"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="form-label mb-1">Matter <span className='pl-px text-red-600'>*</span></label>
                                                        {/* <FormikSelect
                                                    name={`expenses[${index}].matter_id`}
                                                    options={matterData}
                                                    placeholder="Select Matter"
                                                /> */}
                                                        <LazyFormikSelect
                                                            name={`expenses[${index}].matter_id`}
                                                            // options={matterOptions}
                                                            options={matterOptionsByIndex[index] || []}
                                                            placeholder="Select Matter"
                                                            onFocus={() => {
                                                                const contactId = getIn(values, `expenses[${index}].contact_id`);
                                                                loadMattersByIndex(contactId || null, index);
                                                            }}
                                                            isLoading={matterLoadingByIndex[index] === true}
                                                            preloadedOption={initialValues.expenses[0].matter}
                                                        />
                                                        <ErrorMessage name={`expenses[${index}].matter_id`} component="div" className="text-red-500 text-xs mt-1" />
                                                    </div>
                                                    <div>
                                                        <label className="form-label mb-1">Category <span className='pl-px text-red-600'>*</span></label>
                                                        {/* <FormikSelect
                                                    name={`expenses[${index}].category_id`}
                                                    options={itemData}
                                                    placeholder="Select expense category"
                                                /> */}
                                                        <LazyFormikSelect
                                                            name={`expenses[${index}].category_id`}
                                                            options={categoryOptions}
                                                            placeholder="Select Category"
                                                            onFocus={() => loadCategories(null)}
                                                            isLoading={categoryLoading}
                                                            noOptionsMessage={() => (
                                                                isLoading ? (
                                                                    <span className="text-sm">Loading...</span>
                                                                ) : (
                                                                    <div>
                                                                        <span className="text-sm mr-1">No options available</span>
                                                                        <Can I='create' a='category' ability={ability}>
                                                                            <button type="button"
                                                                                onClick={() => setOpen(true)}
                                                                                className="link block mx-auto"
                                                                            >
                                                                                Add new
                                                                            </button>
                                                                        </Can>
                                                                    </div>
                                                                )
                                                            )}
                                                            preloadedOption={initialValues.expenses[index]?.category}
                                                        />
                                                        <ErrorMessage name={`expenses[${index}].category_id`} component="div" className="text-red-500 text-xs mt-1" />
                                                    </div>
                                                    <div className='w-full'>
                                                        <label className="form-label mb-1">Billed By</label>
                                                        {/* <FormikSelect
                                                    name={`expenses[${index}].billed_by`}
                                                    options={userData}
                                                    placeholder="Select Assignee"
                                                /> */}
                                                        <LazyFormikSelect
                                                            name={`expenses[${index}].billed_by`}
                                                            loadOptions={loadUserOptions}
                                                            pageSize={20}
                                                            placeholder="Select Assignee"
                                                            preloadedOption={initialValues.expenses[0].billed_by_user}
                                                            components={{
                                                                Option: CustomOption,
                                                                SingleValue: CustomSingleValue,
                                                            }}
                                                        />
                                                        <ErrorMessage name={`expenses[${index}].billed_by`} component="div" className="text-red-500 text-xs mt-1" />
                                                    </div>
                                                    <div>
                                                        <label className="form-label mb-1">Quantity <span className='pl-px text-red-600'>*</span></label>
                                                        <Field className="input" type="text" name={`expenses[${index}].quantity`} onInput={InputSanitizers} />
                                                        <ErrorMessage name={`expenses[${index}].quantity`} component="div" className="text-red-500 text-xs mt-1" />
                                                    </div>
                                                    <div>
                                                        <label className="form-label mb-1">Rate <span className='pl-px text-red-600'>*</span></label>
                                                        <PriceInput name={`expenses[${index}].rate`} prefix="$" />
                                                        {/* <Field className="input" type="text" name={`expenses[${index}].price`} onInput={InputSanitizers} /> */}
                                                        {/* <ErrorMessage name={`expenses[${index}].price`} component="div" className="text-red-500 text-xs mt-1" /> */}
                                                    </div>

                                                </div>

                                                <div className="grid grid-cols-3 gap-5 mt-5">
                                                    <div className=''>
                                                        <label className="form-label mb-1">Total</label>
                                                        <div className='input'>
                                                            {calculateSubtotal(values.expenses[index].rate, values.expenses[index].quantity)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="form-label mb-1">Date <span className='pl-px text-red-600'>*</span></label>
                                                        <Field name={`expenses[${index}].date`}>
                                                            {({ field, form, meta }) => (
                                                                <div>
                                                                    <DatePicker field={field} form={form} isRange={false} />
                                                                    {meta.touched && meta.error && (
                                                                        <div className="text-red-500 text-xs mt-1">{meta.error}</div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </Field>
                                                    </div>
                                                    <div>
                                                        <label className="form-label mb-1">Status</label>
                                                        <Field as="select" className="input" name={`expenses[${index}].status`}>
                                                            <option value="billable">Billable</option>
                                                            <option value="billed">Billed</option>
                                                            <option value="paid">Paid</option>
                                                            <option value="not billable">Not Billable</option>

                                                        </Field>
                                                    </div>
                                                    <div>
                                                        <DefaultTooltip title={
                                                            <>
                                                                <div>
                                                                    <span>Soft Cost:</span> <br />
                                                                    <span className='font-thin'>
                                                                        Intangible expenses for your clients (travel costs, envelopes, faxes, etc…)
                                                                    </span>
                                                                </div>
                                                                <br />
                                                                <div>
                                                                    <span>Hard Cost:</span> <br />
                                                                    <span className='font-thin'>
                                                                        Expenses you need to pay a third-party vendor for (court filing fees, private investigators, mediators, etc...)
                                                                    </span>
                                                                </div>
                                                            </>
                                                        }>
                                                            <label className="form-label mb-1 items-center gap-1">
                                                                <KeenIcon icon="information" />
                                                                Cost Type
                                                            </label>
                                                        </DefaultTooltip>
                                                        <Field as="select" className="input" name={`expenses[${index}].cost_type`}>
                                                            <option value="soft cost">Soft Cost</option>
                                                            <option value="hard cost">Hard Cost</option>
                                                        </Field>
                                                    </div>
                                                </div>

                                                <div className='grid grid-cols-2 gap-5 mt-5'>
                                                    <div>
                                                        <label className="form-label mb-1">Description</label>
                                                        <Field as="textarea" className="input pt-2 h-[120px]" name={`expenses[${index}].description`} />
                                                    </div>
                                                    <div>
                                                        <label className="form-label mb-1">Note</label>
                                                        <Field as="textarea" className="input pt-2 h-[120px]" name={`expenses[${index}].note`} />
                                                    </div>
                                                </div>


                                                {/* Remove Button */}
                                                {values.expenses.length > 1 && (
                                                    <button type="button" className='btn btn-sm btn-danger btn-outline my-2' onClick={() => remove(index)}>
                                                        <KeenIcon icon="trash" />
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        ))}

                                        {/* Add Button */}
                                        <div className='card-body flex justify-end items-center gap-1'>
                                            {!expenseId && values.expenses.length < 10 && (
                                                <button type="button" className='btn btn-sm' onClick={() => push({
                                                    contact_id: '',
                                                    matter_id: '',
                                                    category_id: '',
                                                    date: '',
                                                    quantity: 1,
                                                    cost_type: 'soft cost',
                                                    price: '',
                                                    billed_by: "",
                                                    billed_by_user: null,
                                                    status: 'billable',
                                                    description: '',
                                                    note: '',
                                                })}>
                                                    Add Form
                                                </button>
                                            )}
                                            <button type="submit" disabled={createContactLoading} className="btn btn-primary">
                                                {createContactLoading ? 'Please Wait...' : 'Create Expense'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </FieldArray>
                        </Form>
                    </Container>
                )}
            </Formik>
            <CategoryModal
                open={open}
                onCloseModal={handleModalClose}
                handleMode={mode}
                data={selectedData}
                onSubmit={handleSubmitCategory}
                createFieldLoading={isLoading}
                updateFieldLoading={loading}
                ref={formikRef}
            />
        </>
    );
};

export { AddExpense };
